import { Controller } from "@hotwired/stimulus";
import { ensureGeocoder, geocodeUsAddress } from "../services/appleMapService";

// The quiz resolves instead of cutting off: the beat holds at least this long,
// and longer while the queued answer POSTs are still in flight.
const COMPLETION_DWELL_MS = 900;

export default class extends Controller {
  static targets = [
    "intro",
    "compare",
    "form",
    "locationInput",
    "geolocateBtn",
    "cardA",
    "cardB",
    "textA",
    "textB",
    "progress",
    "progressBar",
    "counter",
    "back",
    "announcer",
    "dealbreaker",
    "tolerance",
    "complete",
  ];

  static values = {
    pairs: Array,
    location: String,
    geoHint: Object,
    askDealbreaker: Boolean,
  };

  connect() {
    this._keyHandler = (e) => this.handleKeydown(e);
    document.addEventListener("keydown", this._keyHandler);
    this._geoIconHTML = this.geolocateBtnTarget.innerHTML;
    // Preload MapKit so geocoding is ready when user clicks start
    ensureGeocoder().catch(() => {});
  }

  disconnect() {
    document.removeEventListener("keydown", this._keyHandler);
  }

  async start() {
    const location = this.locationInputTarget.value.trim();
    if (!location) {
      this.showLocationError("Please enter a city, neighborhood, or zip code.");
      return;
    }

    this.clearLocationError();

    // Geocode location text via MapKit unless we already have coordinates
    if (!this._geoLat) {
      try {
        // Bias toward the visitor's approximate location (Cloudflare IP geo)
        const result = await geocodeUsAddress(location, this.geoHintValue);
        this._geoLat = result.lat;
        this._geoLng = result.lng;
      } catch {
        this.showLocationError(
          "We couldn't find that location. Try a city name or zip code.",
        );
        return;
      }
    }

    // Shuffle pair order and randomize A/B position
    this.order = this.shuffleArray(this.pairsValue.map((_, i) => i));
    this.flipped = this.order.map(() => Math.random() > 0.5);
    // The progress counter spans the pairs and the screens after them.
    this.totalSteps = this.order.length + (this.askDealbreakerValue ? 2 : 1);

    // The session exists on the server before the first pair shows, so every
    // answer has somewhere to land.
    const response = await this.post("/discover/start", {
      location,
      latitude: this._geoLat,
      longitude: this._geoLng,
      viewport_width: window.innerWidth,
      started_at: Date.now(),
      pair_order: this.order,
      pair_flipped: this.flipped,
    });
    if (!response.ok) {
      const { error } = await response.json().catch(() => ({}));
      this.showLocationError(
        error || "Something went wrong. Please try again.",
      );
      return;
    }

    // Initialize quiz state
    this.currentIdx = 0;
    this.history = [];
    this.animating = false;
    this.queue = Promise.resolve();
    this.pairShownAt = null;

    // Crossfade intro → quiz
    this.introTarget.classList.add("quiz-intro--leaving");
    this.introTarget.addEventListener(
      "animationend",
      () => {
        this.introTarget.classList.add("d-none");
        this.introTarget.classList.remove("quiz-intro--leaving");
        this.progressTarget.classList.remove("d-none");
        this.compareTarget.classList.remove("d-none");
        this.compareTarget.classList.add("quiz-compare--entering");
        this.showCurrentPair();
      },
      { once: true },
    );
  }

  // === Choice actions ===

  chooseA() {
    if (this.animating) return;
    const side = this.flipped[this.currentIdx] ? "b" : "a";
    this.recordChoice("choose", side);
  }

  chooseB() {
    if (this.animating) return;
    const side = this.flipped[this.currentIdx] ? "a" : "b";
    this.recordChoice("choose", side);
  }

  likeBoth() {
    if (this.animating) return;
    this.recordChoice("both", null);
  }

  skip() {
    if (this.animating) return;
    this.recordChoice("skip", null);
  }

  neither() {
    if (this.animating) return;
    this.recordChoice("neither", null);
  }

  undo() {
    // During the answer animation a pending advance() timeout is queued;
    // undoing then would decrement currentIdx and the queued advance() would
    // increment it again, leaving the quiz on the pair just answered instead
    // of the one before it. Ignore undo until the transition settles.
    if (this.animating) return;
    if (this.currentIdx === 0) return;

    const previous = this.history.pop();

    const elapsed = this.elapsedSincePairShown();
    this.currentIdx--;
    this.sendEvent({
      pair_id: this.currentPair().id,
      action: "undo",
      side: null,
      elapsed_ms: elapsed,
    });

    this.showCurrentPair();
    this.showPreviousChoice(previous);
  }

  // === Internal ===

  recordChoice(action, side) {
    const pair = this.currentPair();
    const elapsed = this.elapsedSincePairShown();

    this.sendEvent({ pair_id: pair.id, action, side, elapsed_ms: elapsed });

    // Save for undo
    this.history.push({ action, side });

    // Animate and advance
    this.animating = true;
    this.clearPreviousChoice();
    if (action === "choose") {
      const cardTarget = this.cardFor(side);
      const otherTarget =
        cardTarget === this.cardATarget ? this.cardBTarget : this.cardATarget;
      cardTarget.classList.add("quiz-card--chosen");
      otherTarget.classList.add("quiz-card--dimmed");
    } else if (action === "both") {
      this.cardATarget.classList.add("quiz-card--both", "quiz-card--both-a");
      this.cardBTarget.classList.add("quiz-card--both", "quiz-card--both-b");
    } else if (action === "neither") {
      this.cardATarget.classList.add(
        "quiz-card--neither",
        "quiz-card--neither-a",
      );
      this.cardBTarget.classList.add(
        "quiz-card--neither",
        "quiz-card--neither-b",
      );
    } else {
      this.cardATarget.classList.add("quiz-card--skip");
      this.cardBTarget.classList.add("quiz-card--skip");
    }

    const dwells = { choose: 250, both: 250, neither: 200, skip: 180 };
    setTimeout(() => this.advance(), dwells[action] || 180);
  }

  advance() {
    this.cardATarget.classList.add("quiz-card--exit");
    this.cardBTarget.classList.add("quiz-card--exit");

    setTimeout(() => {
      const classes = [
        "quiz-card--chosen",
        "quiz-card--dimmed",
        "quiz-card--both",
        "quiz-card--both-a",
        "quiz-card--both-b",
        "quiz-card--skip",
        "quiz-card--neither",
        "quiz-card--neither-a",
        "quiz-card--neither-b",
        "quiz-card--exit",
      ];
      this.cardATarget.classList.remove(...classes);
      this.cardBTarget.classList.remove(...classes);

      this.currentIdx++;

      if (this.currentIdx >= this.order.length) {
        this.showPostPairScreens();
        return;
      }

      this.showCurrentPair();
      this.animating = false;
    }, 200);
  }

  showCurrentPair() {
    const pair = this.currentPair();
    const isFlipped = this.flipped[this.currentIdx];

    this.textATarget.textContent = isFlipped ? pair.option_b : pair.option_a;
    this.textBTarget.textContent = isFlipped ? pair.option_a : pair.option_b;

    // Accessibility: label buttons and announce new pair
    this.cardATarget.setAttribute("aria-label", this.textATarget.textContent);
    this.cardBTarget.setAttribute("aria-label", this.textBTarget.textContent);
    this.announcerTarget.textContent = `Question ${this.currentIdx + 1} of ${this.totalSteps}: ${this.textATarget.textContent}, or, ${this.textBTarget.textContent}`;
    this.updateProgress(this.currentIdx);
    // invisible, not d-none: the bar keeps its width when Back appears.
    this.backTarget.classList.toggle("invisible", this.currentIdx === 0);

    // Card enter animation — force reflow so browser paints the initial state
    this.cardATarget.classList.add("quiz-card--enter");
    this.cardBTarget.classList.add("quiz-card--enter");
    void this.cardATarget.offsetHeight;
    this.cardATarget.classList.remove("quiz-card--enter");
    this.cardBTarget.classList.remove("quiz-card--enter");
    this.animating = false;

    this.pairShownAt = performance.now();
  }

  currentPair() {
    return this.pairsValue[this.order[this.currentIdx]];
  }

  // The card showing option `side` of the current pair, given the A/B flip.
  cardFor(side) {
    const showsA = this.flipped[this.currentIdx]
      ? this.cardBTarget
      : this.cardATarget;
    const showsB =
      showsA === this.cardATarget ? this.cardBTarget : this.cardATarget;
    return side === "a" ? showsA : showsB;
  }

  // Undo shows the pair fresh; 23 of 59 re-answers repeated the undone answer
  // (fs20260917), so part of undo use is "what did I pick?". Mark it.
  showPreviousChoice({ action, side }) {
    if (action === "choose") {
      this.cardFor(side).classList.add("quiz-card--chosen");
    } else if (action === "both") {
      this.cardATarget.classList.add("quiz-card--both");
      this.cardBTarget.classList.add("quiz-card--both");
    }
  }

  clearPreviousChoice() {
    this.cardATarget.classList.remove("quiz-card--chosen", "quiz-card--both");
    this.cardBTarget.classList.remove("quiz-card--chosen", "quiz-card--both");
  }

  elapsedSincePairShown() {
    return this.pairShownAt
      ? Math.round(performance.now() - this.pairShownAt)
      : 0;
  }

  // === Server ===

  post(path, body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    return fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify(body),
    });
  }

  // Answers must reach the server in the order they were given, so each POST
  // waits for the previous one. A failed POST is reported through the global
  // handler and must not hold up the answers after it.
  sendEvent(event) {
    this.queue = this.queue
      .then(async () => {
        const response = await this.post("/discover/answers", { event });
        if (!response.ok)
          throw new Error(`POST /discover/answers ${response.status}`);
      })
      .catch((error) => reportError(error));
  }

  // Both post-pair answers are queued before the completion POST, so they
  // are on the server when completion derives from the log.
  submitResults() {
    this.queue.then(() => this.formTarget.requestSubmit());
  }

  // === Post-pair screens ===

  // The compare phase hides with the last pair, which also silences the pair
  // keyboard shortcuts. Back sits in the progress row, outside that phase, so
  // it is hidden here. The dealbreaker screen is skipped while the server says
  // it is held.
  showPostPairScreens() {
    this.compareTarget.classList.add("d-none");
    this.backTarget.classList.add("invisible");
    this.showScreen(
      this.askDealbreakerValue ? this.dealbreakerTarget : this.toleranceTarget,
    );
  }

  chooseDealbreaker(e) {
    if (this.animating) return;
    this.animating = true;
    this.sendEvent({
      action: "dealbreaker",
      value: e.currentTarget.value,
      elapsed_ms: this.elapsedSincePairShown(),
    });
    this.dealbreakerTarget.classList.add("d-none");
    this.showScreen(this.toleranceTarget);
  }

  chooseTolerance(e) {
    if (this.animating) return;
    this.animating = true;
    this.sendEvent({
      action: "tolerance",
      value: e.currentTarget.value,
      elapsed_ms: this.elapsedSincePairShown(),
    });
    this.toleranceTarget.classList.add("d-none");
    this.showCompletion();
  }

  // `animating` stays true from chooseTolerance until the page leaves, and the
  // pair keys are already silent with the compare phase hidden.
  showCompletion() {
    this.progressBarTarget.style.width = "100%";
    this.completeTarget.classList.remove("d-none");
    this.announcerTarget.textContent = `All ${this.order.length} questions answered. Finding your matches…`;
    setTimeout(() => this.submitResults(), COMPLETION_DWELL_MS);
  }

  showScreen(screen) {
    const step =
      screen === this.toleranceTarget ? this.totalSteps - 1 : this.order.length;
    screen.classList.remove("d-none");
    screen.classList.add("quiz-compare--entering");
    const question = screen.querySelector(
      ".quiz-constraint__question",
    ).textContent;
    this.announcerTarget.textContent = `Question ${step + 1} of ${this.totalSteps}: ${question}`;
    this.updateProgress(step);
    this.animating = false;
    this.pairShownAt = performance.now();
  }

  // Accelerating curve: small steps early, bigger jumps later; the last step
  // fills the bar so the end reads as the end.
  updateProgress(step) {
    const linear = step / this.totalSteps;
    const progress = step === this.totalSteps - 1 ? 1 : Math.pow(linear, 1.4);
    this.progressBarTarget.style.width = `${progress * 100}%`;
    this.counterTarget.textContent = `${step + 1} / ${this.totalSteps}`;
  }

  // === Keyboard navigation ===

  handleKeydown(e) {
    if (!this.compareTarget || this.compareTarget.classList.contains("d-none"))
      return;

    // A held key auto-repeats (~every 35ms after ~500ms) and would answer the
    // next question the instant the 450ms transition ends — before the user
    // has seen it. Only accept discrete presses.
    if (e.repeat) return;

    if (e.key === "z" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      this.undo();
      return;
    }

    // Let other modifier combos (Cmd+1, Ctrl+2, etc.) pass through to the browser
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    if (e.key === "ArrowLeft" || e.key === "1") {
      e.preventDefault();
      this.chooseA();
    }
    if (e.key === "ArrowRight" || e.key === "2") {
      e.preventDefault();
      this.chooseB();
    }
    if (e.key === "3" || e.key === "b" || e.key === "B") {
      e.preventDefault();
      this.likeBoth();
    }
    if (e.key === "s" || e.key === "S") {
      e.preventDefault();
      this.skip();
    }
    if (e.key === "n" || e.key === "N") {
      e.preventDefault();
      this.neither();
    }
  }

  geolocate() {
    if (!navigator.geolocation) return;

    const btn = this.geolocateBtnTarget;
    btn.disabled = true;
    btn.innerHTML = "Locating\u2026";

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this._geoLat = pos.coords.latitude;
        this._geoLng = pos.coords.longitude;
        this.locationInputTarget.value = "Your location";
        btn.innerHTML = "Located";
      },
      () => {
        btn.disabled = false;
        btn.innerHTML = this._geoIconHTML;
      },
      { timeout: 10000 },
    );
  }

  resetGeolocate() {
    this.clearLocationError();
    if (!this._geoLat) return;
    this._geoLat = null;
    this._geoLng = null;
    const btn = this.geolocateBtnTarget;
    btn.disabled = false;
    btn.innerHTML = this._geoIconHTML;
  }

  showLocationError(message) {
    this.clearLocationError();
    this.locationInputTarget.classList.add("is-invalid");
    const el = document.createElement("p");
    el.className = "quiz-search-bar__error";
    el.textContent = message;
    // Insert after the search bar
    const bar = this.locationInputTarget.closest(".quiz-search-bar");
    bar.insertAdjacentElement("afterend", el);
  }

  clearLocationError() {
    this.locationInputTarget.classList.remove("is-invalid");
    const existing = document.querySelector(".quiz-search-bar__error");
    if (existing) existing.remove();
  }

  // === Utilities ===

  shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }
}
